// The infinite canvas, with one cursor tool. Wheel pans, ctrl or cmd with the
// wheel zooms (a pinch on a trackpad sends the same thing), dragging empty
// space pans, space-drag pans from anywhere, shift-drag selects. Inside a tile
// the wheel scrolls whatever can scroll, and a press is the widget's: tiles
// drag by any part that is not a control, and snap to each other.

import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { SPEC, TILE_MAX, TILE_MIN } from "../../shared/catalog";
import type { ID, InkItem, Item } from "../../shared/types";
import { addInk, addWidget, commit, transient, updateItem } from "../state/actions";
import { boardItems, get, set, useStore } from "../state/store";
import { cx } from "../lib/util";
import { WidgetTile } from "../widgets/WidgetTile";
import { sizedTo } from "../widgets/fit";
import { camera, screenToWorld, setViewport, takePendingFit, useCamera } from "./camera";
import { bounds, intersects, itemRect, snapMove, snapResize, type Guide, type Handle, type Rect } from "./geometry";
import { InkShapeSvg, InkView, inkColor, measureText, strokePath } from "./ink";
import { openContextMenu } from "../chrome/ContextMenu";

type Gesture =
  | { kind: "pan"; sx: number; sy: number; moved: boolean; clearOnClick: boolean }
  | { kind: "move"; sx: number; sy: number; ids: ID[]; start: Record<ID, Item>; moved: boolean; others: Rect[] }
  | { kind: "resize"; sx: number; sy: number; id: ID; handle: Handle; start: Item; others: Rect[] }
  | { kind: "marquee"; sx: number; sy: number; additive: ID[] }
  | { kind: "draw"; tool: "pen" | "rect" | "ellipse" | "arrow"; pts: number[]; start: { x: number; y: number }; cur: { x: number; y: number }; simulate: boolean }
  | { kind: "erase"; hits: Set<ID> }
  | { kind: "pinch"; d0: number; z0: number; mid: { x: number; y: number }; cam0: { x: number; y: number } };

const INTERACTIVE = 'input, textarea, select, button, a, label, [contenteditable="true"], [data-interactive], iframe, audio, video';

/** How long a wheel gesture keeps to what it began on, past its last turn. */
const WHEEL_HOLD = 300;

/** Whether a box can scroll any further the way the wheel turns. */
function canScroll(el: HTMLElement, dx: number, dy: number): boolean {
  if (el.scrollHeight <= el.clientHeight + 1 && el.scrollWidth <= el.clientWidth + 1) return false;
  const cs = getComputedStyle(el);
  if (dy && /(auto|scroll)/.test(cs.overflowY) && (dy > 0 ? el.scrollTop + el.clientHeight < el.scrollHeight - 1 : el.scrollTop > 0)) return true;
  return !!dx && /(auto|scroll)/.test(cs.overflowX) && (dx > 0 ? el.scrollLeft + el.clientWidth < el.scrollWidth - 1 : el.scrollLeft > 0);
}

/** The box in a tile under the pointer that the wheel scrolls: a list, a table, a note being written. Never the tile or the board. */
function scrollerFor(t: EventTarget | null, dx: number, dy: number): HTMLElement | null {
  for (let el = t as HTMLElement | null; el?.nodeType === 1 && !el.classList.contains("item") && !el.classList.contains("viewport"); el = el.parentElement) {
    if (canScroll(el, dx, dy)) return el;
  }
  return null;
}

function isInteractive(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el?.closest) return false;
  if (el.closest("[data-drag-handle]")) return !!el.closest("button, a, input");
  return !!el.closest(INTERACTIVE);
}

const ItemView = memo(function ItemView({ item, readOnly, editing }: { item: Item; readOnly: boolean; editing: boolean }) {
  const style = { transform: `translate(${item.x}px, ${item.y}px)`, width: item.w, height: item.h, zIndex: item.z };
  // full screen of one widget hides the rest of the board
  const offstage = useStore((s) => !!s.presentIds && !s.presentIds.includes(item.id));
  if (item.kind === "ink") {
    return (
      <div className={cx("item ink", `ink-${item.ink.t}`, item.locked && "is-locked")} data-item-id={item.id} style={style}>
        <InkView
          item={item}
          editing={editing}
          onText={(text) => {
            set({ editing: null });
            const cur = get().items[item.id] as InkItem | undefined;
            if (!cur || cur.ink.t !== "text") return;
            if (!text.trim()) {
              commit([{ op: "item.del", id: item.id }], "Delete label");
              return;
            }
            const m = measureText(text, cur.ink.font, cur.ink.fontSize);
            updateItem(item.id, { ink: { ...cur.ink, text }, w: Math.max(m.w, 40), h: m.h }, "Edit label");
          }}
        />
      </div>
    );
  }
  return (
    <div className={cx("item widget", `type-${item.type}`, item.locked && "is-locked", offstage && "is-offstage")} data-item-id={item.id} style={style}>
      <WidgetTile item={item} readOnly={readOnly} />
    </div>
  );
});

/** layer: what a typed board drawn on a canvas (a mind map) puts in the board's world, beneath its widgets. */
export function Canvas({ readOnly = false, boardIdOverride, layer }: { readOnly?: boolean; boardIdOverride?: ID; layer?: React.ReactNode }) {
  const viewRef = useRef<HTMLDivElement>(null);
  const cam = useCamera();
  const boardId = useStore((s) => boardIdOverride ?? s.boardId);
  const items = useStore(useShallow((s) => boardItems(s, boardIdOverride ?? s.boardId)));
  const tool = useStore((s) => (readOnly ? "select" : s.tool));
  const editing = useStore((s) => s.editing);
  const gesture = useRef<Gesture | null>(null);
  const captureId = useRef<number | null>(null);
  const capture = () => {
    if (captureId.current === null) return;
    try {
      viewRef.current?.setPointerCapture(captureId.current);
    } catch {
      /* the pointer already went away */
    }
    captureId.current = null;
  };
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const space = useRef(false);
  const [guides, setGuides] = useState<Guide[]>([]);
  const [marquee, setMarquee] = useState<Rect | null>(null);
  const [draft, setDraft] = useState<Extract<Gesture, { kind: "draw" }> | null>(null);
  const [panning, setPanning] = useState(false);
  const [resizeLabel, setResizeLabel] = useState<string | null>(null);

  // viewport size
  useLayoutEffect(() => {
    const el = viewRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setViewport(el.clientWidth, el.clientHeight));
    ro.observe(el);
    setViewport(el.clientWidth, el.clientHeight);
    return () => ro.disconnect();
  }, []);

  // first visit to a board on this device: fit its content
  useEffect(() => {
    if (!boardId) return;
    if (takePendingFit(boardId) || camera.board() !== boardId) {
      if (camera.board() !== boardId) camera.restore(boardId, bounds(items.map(itemRect)));
      else camera.fitSmart(bounds(items.map(itemRect)), { animate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, items.length > 0]);

  // wheel: pan, or zoom with ctrl/cmd (and trackpad pinch). Over a widget that can scroll, the widget scrolls.
  // A turn of the wheel keeps to what it began on until it rests: a list scrolled to its end does not hand
  // the rest of a flick to the board, and the board moving does not stop on a list that slides under the pointer.
  useEffect(() => {
    const el = viewRef.current;
    if (!el) return;
    let held: { box: HTMLElement | null; at: number } | null = null;
    const onWheel = (e: WheelEvent) => {
      const zoom = e.ctrlKey || e.metaKey;
      if (!zoom) {
        // Shift and the wheel go across, as the browser scrolls a box; a trackpad's swipe goes the way it mostly goes
        const [x, y] = e.shiftKey && !e.deltaX ? [e.deltaY, 0] : [e.deltaX, e.deltaY];
        const [dx, dy] = Math.abs(x) > Math.abs(y) ? [x, 0] : [0, y];
        const now = performance.now();
        // a box holds the wheel only while the pointer stays on it; the board, which moves under the pointer, by time alone
        const on = held && now - held.at < WHEEL_HOLD && (!held.box || held.box.contains(e.target as Node)) ? held : null;
        if (on?.box) {
          on.at = now;
          if (on.box.isConnected && canScroll(on.box, dx, dy)) return; // the browser scrolls it
          e.preventDefault();
          return;
        }
        const box = on ? null : scrollerFor(e.target, dx, dy);
        held = { box, at: now };
        if (box) return;
      }
      e.preventDefault();
      if (zoom) {
        const unit = e.deltaMode === 1 ? 0.05 : 0.0024;
        camera.zoomAt(e.clientX, e.clientY - el.getBoundingClientRect().top, Math.exp(-e.deltaY * unit));
      } else {
        const k = e.deltaMode === 1 ? 32 : 1;
        if (e.shiftKey && !e.deltaX) camera.panBy(-e.deltaY * k, 0);
        else camera.panBy(-e.deltaX * k, -e.deltaY * k);
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    // Safari trackpad pinch
    const onGesture = (e: Event) => e.preventDefault();
    el.addEventListener("gesturestart", onGesture);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("gesturestart", onGesture);
    };
  }, []);

  // space to pan
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code === "Space" && !(e.target as HTMLElement)?.closest?.("input, textarea, [contenteditable]")) {
        if (!space.current) {
          space.current = true;
          viewRef.current?.classList.add("space-pan");
        }
        e.preventDefault();
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        space.current = false;
        viewRef.current?.classList.remove("space-pan");
      }
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  const local = useCallback((e: { clientX: number; clientY: number }) => {
    const r = viewRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    const el = viewRef.current!;
    const p = local(e);
    pointers.current.set(e.pointerId, p);
    if (pointers.current.size === 2) {
      // second finger: pinch zoom
      const [a, b] = [...pointers.current.values()];
      const c = useCamera.getState();
      gesture.current = { kind: "pinch", d0: Math.hypot(a.x - b.x, a.y - b.y), z0: c.z, mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, cam0: { x: c.x, y: c.y } };
      return;
    }
    const target = e.target as HTMLElement;
    const handle = target.closest?.("[data-handle]") as HTMLElement | null;
    const itemEl = target.closest?.("[data-item-id]") as HTMLElement | null;
    const id = itemEl?.dataset.itemId ?? null;
    const st = get();

    // Pans and moves take the pointer only once they really move, so a click
    // or a double-click still reaches whatever is under it.
    const startPan = (clearOnClick: boolean) => {
      gesture.current = { kind: "pan", sx: e.clientX, sy: e.clientY, moved: false, clearOnClick };
      captureId.current = e.pointerId;
      setPanning(true);
    };

    if (e.button === 1 || (e.button === 0 && space.current) || (readOnly && e.button === 0 && !isInteractive(target))) {
      e.preventDefault();
      startPan(false);
      return;
    }
    if (e.button !== 0 || readOnly) return;

    if (tool === "pen" || tool === "rect" || tool === "ellipse" || tool === "arrow") {
      const w = screenToWorld(p.x, p.y);
      const g: Gesture = { kind: "draw", tool, pts: [w.x, w.y, e.pressure || 0.5], start: w, cur: w, simulate: e.pointerType !== "pen" };
      gesture.current = g;
      setDraft(g);
      el.setPointerCapture(e.pointerId);
      return;
    }
    if (tool === "text") {
      const w = screenToWorld(p.x, p.y);
      const ink = st.ink;
      const fs = ink.fontSize;
      const created = addInk({ t: "text", text: "", color: ink.color, fontSize: fs, font: ink.font, opacity: 1 }, { x: Math.round(w.x), y: Math.round(w.y - fs * 0.6), w: 160, h: Math.round(fs * 1.4) });
      if (created) set({ editing: created.id, selection: [created.id], tool: "select" });
      e.preventDefault();
      return;
    }
    if (tool === "eraser") {
      gesture.current = { kind: "erase", hits: new Set() };
      el.setPointerCapture(e.pointerId);
      eraseAt(p);
      return;
    }

    // select tool
    if (handle && st.selection.length === 1) {
      const it = st.items[st.selection[0]];
      if (it && !it.locked) {
        e.preventDefault();
        gesture.current = {
          kind: "resize",
          sx: e.clientX,
          sy: e.clientY,
          id: it.id,
          handle: handle.dataset.handle as Handle,
          start: it,
          others: items.filter((o) => o.id !== it.id && o.kind === "widget").map(itemRect),
        };
        el.setPointerCapture(e.pointerId);
        return;
      }
    }
    if (id && st.items[id]) {
      if (st.editing === id) return;
      // a control inside a tile: select the tile, let the control work. A Button is a button over its whole tile
      // (Round 7), so a drag on it still moves it, and a click that never moved presses it
      if (isInteractive(target) && !target.closest?.("[data-drags]")) {
        if (!st.selection.includes(id)) set({ selection: [id] });
        return;
      }
      let selection = st.selection;
      if (e.shiftKey) selection = selection.includes(id) ? selection.filter((x) => x !== id) : [...selection, id];
      else if (!selection.includes(id)) selection = [id];
      set({ selection, editing: null });
      const ids = selection.filter((x) => st.items[x] && !st.items[x].locked);
      if (!ids.length) return;
      const start: Record<ID, Item> = {};
      for (const x of ids) start[x] = st.items[x];
      gesture.current = { kind: "move", sx: e.clientX, sy: e.clientY, ids, start, moved: false, others: items.filter((o) => !ids.includes(o.id) && o.kind === "widget").map(itemRect) };
      captureId.current = e.pointerId;
      return;
    }
    // empty canvas
    if (st.editing) set({ editing: null });
    if (e.shiftKey) {
      gesture.current = { kind: "marquee", sx: p.x, sy: p.y, additive: st.selection };
      el.setPointerCapture(e.pointerId);
      return;
    }
    startPan(true);
  };

  const eraseAt = (p: { x: number; y: number }) => {
    const g = gesture.current;
    if (!g || g.kind !== "erase") return;
    const w = screenToWorld(p.x, p.y);
    const r = 6 / useCamera.getState().z;
    for (const it of items) {
      if (it.kind !== "ink" || it.locked || g.hits.has(it.id)) continue;
      if (w.x >= it.x - r && w.x <= it.x + it.w + r && w.y >= it.y - r && w.y <= it.y + it.h + r) {
        g.hits.add(it.id);
        transient({ [it.id]: { ...it } });
        document.querySelector(`[data-item-id="${it.id}"]`)?.classList.add("erasing");
      }
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const p = local(e);
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, p);
    const g = gesture.current;
    if (!g) return;
    const z = useCamera.getState().z;
    switch (g.kind) {
      case "pinch": {
        const pts = [...pointers.current.values()];
        if (pts.length < 2) return;
        const [a, b] = pts;
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const nz = Math.min(2.5, Math.max(0.15, (g.z0 * d) / g.d0));
        const k = nz / g.z0;
        useCamera.setState({ z: nz, x: mid.x - (g.mid.x - g.cam0.x) * k, y: mid.y - (g.mid.y - g.cam0.y) * k });
        return;
      }
      case "pan": {
        const dx = e.clientX - g.sx;
        const dy = e.clientY - g.sy;
        if (!g.moved && Math.hypot(dx, dy) < 3) return;
        if (!g.moved) capture();
        g.moved = true;
        camera.panBy(dx, dy);
        g.sx = e.clientX;
        g.sy = e.clientY;
        return;
      }
      case "move": {
        const dx = (e.clientX - g.sx) / z;
        const dy = (e.clientY - g.sy) / z;
        if (!g.moved && Math.hypot(dx * z, dy * z) < 3) return;
        if (!g.moved) {
          document.body.classList.add("is-dragging");
          capture();
        }
        g.moved = true;
        const box = bounds(g.ids.map((id) => itemRect(g.start[id])))!;
        const moving = { ...box, x: box.x + dx, y: box.y + dy };
        const snapped = e.altKey ? { dx: 0, dy: 0, guides: [] } : snapMove(moving, g.others, 6 / z);
        const patches: Record<ID, Partial<Item>> = {};
        for (const id of g.ids) patches[id] = { x: Math.round(g.start[id].x + dx + snapped.dx), y: Math.round(g.start[id].y + dy + snapped.dy) };
        transient(patches);
        setGuides(snapped.guides);
        return;
      }
      case "resize": {
        const dx = (e.clientX - g.sx) / z;
        const dy = (e.clientY - g.sy) / z;
        const min: [number, number] = g.start.kind === "widget" ? SPEC[g.start.type].min : [TILE_MIN, TILE_MIN];
        const keep = e.shiftKey || (g.start.kind === "widget" && (g.start.type === "image" || g.start.type === "clock") && e.shiftKey);
        const r = snapResize(itemRect(g.start), g.handle, dx, dy, min, g.others, 6 / z, keep);
        // no bigger than the board keeps a tile, so it is drawn at the size it is saved at; the edge held still stays put
        const rect = { ...r.rect };
        if (rect.w > TILE_MAX) {
          if (g.handle.includes("w")) rect.x += rect.w - TILE_MAX;
          rect.w = TILE_MAX;
        }
        if (rect.h > TILE_MAX) {
          if (g.handle.includes("n")) rect.y += rect.h - TILE_MAX;
          rect.h = TILE_MAX;
        }
        transient({ [g.id]: rect });
        setGuides(r.guides);
        setResizeLabel(`${rect.w} × ${rect.h}`);
        return;
      }
      case "marquee": {
        const rect = { x: Math.min(g.sx, p.x), y: Math.min(g.sy, p.y), w: Math.abs(p.x - g.sx), h: Math.abs(p.y - g.sy) };
        setMarquee(rect);
        const a = screenToWorld(rect.x, rect.y);
        const b = screenToWorld(rect.x + rect.w, rect.y + rect.h);
        const wr = { x: a.x, y: a.y, w: b.x - a.x, h: b.y - a.y };
        const hit = items.filter((i) => intersects(wr, itemRect(i))).map((i) => i.id);
        set({ selection: [...new Set([...g.additive, ...hit])] });
        return;
      }
      case "draw": {
        const w = screenToWorld(p.x, p.y);
        if (g.tool === "pen") {
          const events = (e.nativeEvent as PointerEvent).getCoalescedEvents?.() ?? [e.nativeEvent];
          for (const ce of events) {
            const lp = local(ce);
            const wp = screenToWorld(lp.x, lp.y);
            g.pts.push(wp.x, wp.y, ce.pressure || 0.5);
          }
        }
        g.cur = e.shiftKey && (g.tool === "rect" || g.tool === "ellipse") ? squareFrom(g.start, w) : w;
        setDraft({ ...g });
        return;
      }
      case "erase":
        eraseAt(p);
        return;
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    captureId.current = null;
    const g = gesture.current;
    gesture.current = null;
    document.body.classList.remove("is-dragging");
    setPanning(false);
    setGuides([]);
    setResizeLabel(null);
    if (!g) return;
    const st = get();
    switch (g.kind) {
      case "pan":
        if (!g.moved && g.clearOnClick) set({ selection: [], editing: null });
        break;
      case "move":
        if (g.moved) {
          const ops = g.ids.map((id) => ({ op: "item.put" as const, item: st.items[id] }));
          const undoOps = g.ids.map((id) => ({ op: "item.put" as const, item: g.start[id] }));
          commit(ops, "Move", { undo: undoOps });
        }
        break;
      case "resize": {
        const item = st.items[g.id];
        // a press on a handle that changed nothing is no resize: nothing to save or undo
        if (item && (item.x !== g.start.x || item.y !== g.start.y || item.w !== g.start.w || item.h !== g.start.h)) {
          let next = item;
          if (item.kind === "ink" && item.ink.t === "text") {
            // a resized label scales its writing
            const k = item.h / g.start.h;
            next = { ...item, ink: { ...item.ink, fontSize: Math.max(8, Math.round(item.ink.fontSize * k)) } };
          }
          // a size the user picked is theirs: the board stops fitting an agent's widget to its content
          next = sizedTo({ ...next, w: g.start.w, h: g.start.h }, next.w, next.h);
          commit([{ op: "item.put", item: next }], "Resize", { undo: [{ op: "item.put", item: g.start }] });
        }
        break;
      }
      case "marquee":
        setMarquee(null);
        break;
      case "draw":
        setDraft(null);
        finishDraw(g);
        break;
      case "erase":
        if (g.hits.size) commit([...g.hits].map((id) => ({ op: "item.del" as const, id })), "Erase");
        break;
    }
  };

  const finishDraw = (g: Extract<Gesture, { kind: "draw" }>) => {
    const st = get();
    const ink = st.ink;
    if (g.tool === "pen") {
      if (g.pts.length < 6) return;
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      for (let i = 0; i < g.pts.length; i += 3) {
        minX = Math.min(minX, g.pts[i]);
        maxX = Math.max(maxX, g.pts[i]);
        minY = Math.min(minY, g.pts[i + 1]);
        maxY = Math.max(maxY, g.pts[i + 1]);
      }
      const pad = ink.size * 2 + 2;
      const x = Math.floor(minX - pad);
      const y = Math.floor(minY - pad);
      const w = Math.ceil(maxX - minX + pad * 2);
      const h = Math.ceil(maxY - minY + pad * 2);
      const pts: number[] = [];
      for (let i = 0; i < g.pts.length; i += 3) pts.push(+(g.pts[i] - x).toFixed(1), +(g.pts[i + 1] - y).toFixed(1), +g.pts[i + 2].toFixed(2));
      addInk({ t: "path", pts, vw: w, vh: h, color: ink.color, size: ink.size * 2.2, opacity: ink.opacity }, { x, y, w, h });
      return;
    }
    const a = g.start;
    const b = g.cur;
    let w = Math.abs(b.x - a.x);
    let h = Math.abs(b.y - a.y);
    if (g.tool === "arrow") {
      if (Math.hypot(w, h) < 8) return;
      const pad = 0;
      const x = Math.min(a.x, b.x) - pad;
      const y = Math.min(a.y, b.y) - pad;
      w = Math.max(1, w);
      h = Math.max(1, h);
      addInk(
        { t: "arrow", x1: (a.x - x) / w, y1: (a.y - y) / h, x2: (b.x - x) / w, y2: (b.y - y) / h, color: ink.color, size: ink.size, opacity: ink.opacity, dash: ink.dash },
        { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) },
      );
      return;
    }
    let x = Math.min(a.x, b.x);
    let y = Math.min(a.y, b.y);
    if (w < 8 && h < 8) {
      w = 200;
      h = 128;
      x = a.x - w / 2;
      y = a.y - h / 2;
    }
    addInk({ t: g.tool, color: ink.color, fill: ink.fill, size: ink.size, opacity: ink.opacity, dash: ink.dash }, { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) });
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    if (readOnly) return;
    // ask what is really under the pointer: a captured pointer retargets events
    const under = (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null) ?? (e.target as HTMLElement);
    const itemEl = under.closest?.("[data-item-id]") as HTMLElement | null;
    const st = get();
    if (itemEl) {
      const it = st.items[itemEl.dataset.itemId!];
      if (!it || it.locked) return;
      // a widget's settings open on a double-click, and a single click is left to the widget. Text that is
      // typed into (a note, a sticky, a heading) is edited on a double-click, and opens its settings from its title bar
      const onHead = !!under.closest?.(".tile-head");
      if ((it.kind === "ink" && it.ink.t === "text") || (it.kind === "widget" && !onHead && ["notes", "sticky", "heading"].includes(it.type))) {
        e.preventDefault();
        set({ editing: it.id, selection: [it.id] });
      } else if (it.kind === "widget" && (onHead || !isInteractive(under))) {
        e.preventDefault();
        set({ selection: [it.id], inspect: true, editing: null });
      }
      return;
    }
    if (st.tool !== "select") return;
    const p = local(e);
    const w = screenToWorld(p.x, p.y);
    set({ panel: "gallery", panelArg: { at: w } });
  };

  const onContextMenu = (e: React.MouseEvent) => {
    if (readOnly) return;
    e.preventDefault();
    const target = e.target as HTMLElement;
    const itemEl = target.closest?.("[data-item-id]") as HTMLElement | null;
    const st = get();
    const p = local(e);
    const at = screenToWorld(p.x, p.y);
    if (itemEl) {
      const id = itemEl.dataset.itemId!;
      if (!st.selection.includes(id)) set({ selection: [id] });
      openContextMenu({ x: e.clientX, y: e.clientY, kind: "item", ids: get().selection, at });
    } else openContextMenu({ x: e.clientX, y: e.clientY, kind: "canvas", ids: [], at });
  };

  // drop an image file to make an image widget
  const onDrop = (e: React.DragEvent) => {
    if (readOnly) return;
    const file = [...e.dataTransfer.files].find((f) => f.type.startsWith("image/"));
    const url = e.dataTransfer.getData("text/uri-list") || e.dataTransfer.getData("text/plain");
    const p = local(e);
    const at = screenToWorld(p.x, p.y);
    if (file) {
      e.preventDefault();
      void import("../widgets/media").then(({ fileToDataUrl }) =>
        fileToDataUrl(file).then((src) => addWidget("image", { title: file.name.replace(/\.[^.]+$/, "").slice(0, 40) || "Image", props: { src, fit: "cover" }, at: { x: at.x - 160, y: at.y - 120 } })),
      );
    } else if (/^https?:\/\//.test(url)) {
      e.preventDefault();
      addWidget("embed", { props: { url }, at: { x: at.x - 240, y: at.y - 160 } });
    }
  };

  const gridOn = useStore((s) => {
    const b = s.boards.find((x) => x.id === (boardIdOverride ?? s.boardId));
    if (!b) return false;
    return b.theme.grid ?? (b.theme.id === "studio" || b.theme.id === "paper");
  });
  const gridSize = 24 * cam.z;
  const gridStyle = gridOn && cam.z > 0.3 ? { backgroundSize: `${gridSize}px ${gridSize}px`, backgroundPosition: `${cam.x}px ${cam.y}px` } : undefined;

  return (
    <div
      ref={viewRef}
      className={cx("viewport", !readOnly && `tool-${tool}`, panning && "is-panning", gridOn && "has-grid", readOnly && "is-readonly")}
      style={gridStyle}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      onDragOver={(e) => !readOnly && e.preventDefault()}
      onDrop={onDrop}
    >
      <div className="world" style={{ transform: `translate3d(${cam.x}px, ${cam.y}px, 0) scale(${cam.z})` }}>
        {layer}
        {items.map((it) => (
          <ItemView key={it.id} item={it} readOnly={readOnly} editing={editing === it.id} />
        ))}
        {draft && <DraftView draft={draft} />}
      </div>
      {!readOnly && <SelectionOverlay resizeLabel={resizeLabel} />}
      {guides.length > 0 && <GuidesOverlay guides={guides} />}
      {marquee && <div className="marquee" style={{ left: marquee.x, top: marquee.y, width: marquee.w, height: marquee.h }} />}
    </div>
  );
}

function squareFrom(a: { x: number; y: number }, b: { x: number; y: number }) {
  const d = Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y));
  return { x: a.x + Math.sign(b.x - a.x || 1) * d, y: a.y + Math.sign(b.y - a.y || 1) * d };
}

function DraftView({ draft }: { draft: Extract<Gesture, { kind: "draw" }> }) {
  const ink = useStore((s) => s.ink);
  if (draft.tool === "pen") {
    return (
      <svg className="ink-draft" style={{ position: "absolute", left: 0, top: 0, overflow: "visible", pointerEvents: "none" }} width={1} height={1}>
        <path d={strokePath(draft.pts, ink.size * 2.2, draft.simulate)} style={{ fill: inkColor(ink.color) }} opacity={ink.opacity} />
      </svg>
    );
  }
  const a = draft.start;
  const b = draft.cur;
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const w = Math.max(1, Math.abs(b.x - a.x));
  const h = Math.max(1, Math.abs(b.y - a.y));
  const shape =
    draft.tool === "arrow"
      ? ({ t: "arrow", x1: (a.x - x) / w, y1: (a.y - y) / h, x2: (b.x - x) / w, y2: (b.y - y) / h, color: ink.color, size: ink.size, opacity: ink.opacity, dash: ink.dash } as const)
      : ({ t: draft.tool, color: ink.color, fill: ink.fill, size: ink.size, opacity: ink.opacity, dash: ink.dash } as const);
  return (
    <div style={{ position: "absolute", transform: `translate(${x}px, ${y}px)`, width: w, height: h, pointerEvents: "none" }}>
      <InkShapeSvg ink={shape} w={w} h={h} />
    </div>
  );
}

const HANDLES: Handle[] = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

function SelectionOverlay({ resizeLabel }: { resizeLabel: string | null }) {
  const cam = useCamera();
  const sel = useStore(useShallow((s) => s.selection.map((id) => s.items[id]).filter(Boolean)));
  const editing = useStore((s) => s.editing);
  if (!sel.length) return null;
  const box = bounds(sel.map(itemRect))!;
  const r = { left: box.x * cam.z + cam.x, top: box.y * cam.z + cam.y, width: box.w * cam.z, height: box.h * cam.z };
  const single = sel.length === 1 ? sel[0] : null;
  const locked = sel.every((i) => i.locked);
  return (
    <div className={cx("selection", locked && "is-locked", editing && "is-editing")} style={r}>
      {sel.length > 1 &&
        sel.map((i) => (
          <div key={i.id} className="selection-item" style={{ left: (i.x - box.x) * cam.z - 1, top: (i.y - box.y) * cam.z - 1, width: i.w * cam.z + 2, height: i.h * cam.z + 2 }} />
        ))}
      {single && !single.locked && !editing && HANDLES.map((h) => <div key={h} className={`handle handle-${h}`} data-handle={h} />)}
      {resizeLabel && <div className="size-label">{resizeLabel}</div>}
      {locked && <div className="lock-badge">Locked</div>}
    </div>
  );
}

function GuidesOverlay({ guides }: { guides: Guide[] }) {
  const cam = useCamera();
  return (
    <svg className="guides" width="100%" height="100%">
      {guides.map((g, i) =>
        g.axis === "x" ? (
          <line key={i} x1={g.pos * cam.z + cam.x} x2={g.pos * cam.z + cam.x} y1={g.from * cam.z + cam.y - 8} y2={g.to * cam.z + cam.y + 8} />
        ) : (
          <line key={i} y1={g.pos * cam.z + cam.y} y2={g.pos * cam.z + cam.y} x1={g.from * cam.z + cam.x - 8} x2={g.to * cam.z + cam.x + 8} />
        ),
      )}
    </svg>
  );
}
